import useAxios from '@core/hooks/useFetch';
import { Content, ContentTypeEnum, MethodsEnum, OperationsEnum } from '@core/models';

import { paths, components } from './api';

const GET_SURVEYS = '/api/Survey/GetAll';
const GET_EXTENDED_SURVEYS = '/api/Survey/GetAllExtended';
const GET_SURVEY_DETAILS = '/api/Survey/GetSurveyDetails/';
const GET_SURVEY_INITIAL_DATA = '/api/Survey/GetInitialData';
const GET_SURVEY_DEFAULT_DEMOGRAPHIC = '/api/CountrySettings/GetDefaultDemographicGroups';
const GET_SURVEY_DEFAULT_AGE_GROUPS = '/api/CountrySettings/GetDefaultAgeGroups';
const GET_LAUNCH_SURVEY = '/api/Survey/LaunchSurvey/';
const POST_CLOSE_SURVEY = '/api/Survey/CloseSurvey';
const POST_CREATE_SURVEY = '/api/Survey/CreateSurvey';
const POST_UPDATE_SURVEY = '/api/Survey/UpdateSurvey';
const POST_DELETE_RESPONDENTS = '/api/Survey/DeleteRespondents';
const POST_RESTART_DATA_GENERATION = '/api/Survey/RegenerateSurvey';

type GetSurveys = paths[typeof GET_SURVEYS][MethodsEnum.GET];
export type GetSurveysResponse = GetSurveys[OperationsEnum.RESPONSES]['200'][Content][ContentTypeEnum.Json];
export type Survey = components['schemas']['SurveysBaseListDto'];

export function useGetSurveys() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<GetSurveysResponse>({
        url: GET_SURVEYS,
        method: MethodsEnum.GET,
    });

    return { data, isLoading, error, doFetch } as const;
}

type GetExtendedSurveys = paths[typeof GET_EXTENDED_SURVEYS][MethodsEnum.GET];
export type GetExtendedSurveysResponse = GetExtendedSurveys[OperationsEnum.RESPONSES]['200'][Content][ContentTypeEnum.Json];
export type ExtendedSurvey = components['schemas']['SurveysExtendedListDto'];

export function useGetExtendedSurveys() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<GetExtendedSurveysResponse>({
        url: GET_EXTENDED_SURVEYS,
        method: MethodsEnum.GET,
    });

    return { data, isLoading, error, doFetch } as const;
}

type GetSurveyDetails = paths[`/api/Survey/GetSurveyDetails/${number}`][MethodsEnum.GET];
export type GetSurveyDetailsResponse = GetSurveyDetails[OperationsEnum.RESPONSES]['200'][Content][ContentTypeEnum.Json];
export type SurveyDetailsItem = components['schemas']['SurveyDetailsItem'];
export type SurveyDetails = components['schemas']['SurveyDetails'];
export type RequiredCountryId = NonNullable<SurveyDetails['countryId']>;
export type RequiredCustomFielding = NonNullable<SurveyDetails['customFielding']>;
export type SurveyDetailsSampleProvider = components['schemas']['SurveyDetailsSampleProvider'];
export type SurveyDetailsSampleProviderExtended = components["schemas"]["SurveyDetailsSampleProviderNameCmxID"];

export function useGetSurveyDetails(surveyId?: string, lazy = false) {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<GetSurveyDetailsResponse>({
        url: `${GET_SURVEY_DETAILS}${surveyId}`,
        method: MethodsEnum.GET,
        lazy,
    });

    return { data, isLoading, error, doFetch } as const;
}

type GetSurveyInitialData = paths[typeof GET_SURVEY_INITIAL_DATA][MethodsEnum.GET];
export type GetSurveyInitialDataResponse =
    GetSurveyInitialData[OperationsEnum.RESPONSES]['200'][Content][ContentTypeEnum.Json];

export function useGetSurveyInitialData() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<GetSurveyInitialDataResponse>({
        url: GET_SURVEY_INITIAL_DATA,
        method: MethodsEnum.GET,
    });

    return { data, isLoading, error, doFetch } as const;
}

type GetSurveyDefaultDemographic = paths[typeof GET_SURVEY_DEFAULT_DEMOGRAPHIC][MethodsEnum.GET];
export type GetSurveyDefaultDemographicResponse = GetSurveyDefaultDemographic[OperationsEnum.RESPONSES]['200'][Content][ContentTypeEnum.Json];
export type GetSurveyDefaultDemographicParams = GetSurveyDefaultDemographic[OperationsEnum.PARAMETERS]['query'];

export function useGetSurveyDefaultDemographic() {
    const {
        data,
        isLoading,
        error,
        doFetch,
    } = useAxios<GetSurveyDefaultDemographicResponse, GetSurveyDefaultDemographicParams>({
        url: GET_SURVEY_DEFAULT_DEMOGRAPHIC,
        lazy: true,
    });

    return { data, isLoading, error, doFetch } as const;
}

type GetSurveyDefaultAgeGroups = paths[typeof GET_SURVEY_DEFAULT_AGE_GROUPS][MethodsEnum.GET];
export type GetSurveyDefaultAgeGroupsResponse = GetSurveyDefaultAgeGroups[OperationsEnum.RESPONSES]['200'][Content][ContentTypeEnum.Json];
export type GetSurveyDefaultAgeGroupsParams = GetSurveyDefaultAgeGroups[OperationsEnum.PARAMETERS]['query'];

export function useGetSurveyDefaultAgeGroups() {
    const {
        data,
        isLoading,
        error,
        doFetch,
    } = useAxios<GetSurveyDefaultAgeGroupsResponse, GetSurveyDefaultAgeGroupsParams>({
        url: GET_SURVEY_DEFAULT_AGE_GROUPS,
        lazy: true,
    });

    return { data, isLoading, error, doFetch } as const;
}

type CloseSurvey = paths[typeof POST_CLOSE_SURVEY][MethodsEnum.POST];
export type CloseSurveyParams = NonNullable<CloseSurvey[OperationsEnum.PARAMETERS]['query']>;

export function useCloseSurvey() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<string, CloseSurveyParams>({
        url: POST_CLOSE_SURVEY,
        method: MethodsEnum.POST,
        lazy: true,
    });

    return { data, isLoading, error, doFetch } as const;
}

export function useLaunchSurvey(surveyId: string) {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<string, CloseSurveyParams>({
        url: `${GET_LAUNCH_SURVEY}${surveyId}`,
        lazy: true,
    });

    return { data, isLoading, error, doFetch } as const;
}

type UpdateSurvey = paths[typeof POST_UPDATE_SURVEY][MethodsEnum.POST];
export type BodyUpdateSurvey = NonNullable<UpdateSurvey[OperationsEnum.REQUEST]>[Content][ContentTypeEnum.Json];

export function useUpdateSurvey() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<string, BodyUpdateSurvey>({
        url: POST_UPDATE_SURVEY,
        method: MethodsEnum.POST,
        lazy: true,
    });

    return { data, isLoading, error, doFetch } as const;
}

type CreateSurvey = paths[typeof POST_CREATE_SURVEY][MethodsEnum.POST];
export type BodyCreateSurvey = NonNullable<CreateSurvey[OperationsEnum.REQUEST]>[Content][ContentTypeEnum.Json];

export function useCreateSurvey() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<string, BodyCreateSurvey>({
        url: POST_CREATE_SURVEY,
        method: MethodsEnum.POST,
        lazy: true,
    });

    return { data, isLoading, error, doFetch } as const;
}


type DeleteRespondents = paths[typeof POST_DELETE_RESPONDENTS][MethodsEnum.POST];
type DeleteRespondentsResponse = DeleteRespondents[OperationsEnum.RESPONSES]['200'][Content][ContentTypeEnum.Json];
export type DeleteRespondentsParams = FormData;

export function useDeleteRespondents() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<DeleteRespondentsResponse, DeleteRespondentsParams>({
        url: POST_DELETE_RESPONDENTS,
        method: MethodsEnum.POST,
        lazy: true,
        initialConfig: {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        },
    });

    return { data, isLoading, error, doFetch } as const;
}

type RegenerateSurvey = paths[typeof POST_RESTART_DATA_GENERATION][MethodsEnum.GET];
export type RegenerateSurveyParams = NonNullable<RegenerateSurvey[OperationsEnum.PARAMETERS]['query']>;

export function useRestartDataGeneration() {
    const {
        data,
        isLoading,
        error,
        doFetch
    } = useAxios<string, RegenerateSurveyParams>({
        url: POST_RESTART_DATA_GENERATION,
        method: MethodsEnum.GET,
        lazy: true,
    })

    return { data, isLoading, error, doFetch } as const;
}
